import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import DataTableComponent from "../../common/DataTableComponent";
import { GetFirstAidUsageReport } from "../../../services/FirstAidBoxService";
import { useFirstAidReportFilters, useFirstAidUsageReport } from "../../../query/hooks/QueryHooksFirstAidBox";
import { OHS_BASE_PATH, REPORT_DATE_TIME_FORMAT } from "./ohsConstants";
import { ConditionBadge, MutedText } from "./Ohs.styles";
import { describeTagCheck, exportOhsReport, getPrefixedUser } from "./ohsUtils";
import OhsUserCell from "./OhsUserCell";

const EMPTY_FILTER = { arr: [], checkObj: {} };
// Search-style filters keep their ticked options in an array, not an object.
const EMPTY_SEARCH_FILTER = { arr: [], checkObj: [] };
const USAGE_EXPORT_COLUMNS = [
    { label: "Date", width: 12 },
    { label: "Time", width: 7 },
    { label: "Used By", width: 20 },
    { label: "Campus", width: 14 },
    { label: "Building", width: 22 },
    { label: "Area", width: 18 },
    { label: "Box", width: 16 },
    { label: "Tag Removed", width: 13 },
    { label: "New Tag Fitted", width: 14 },
    { label: "Tag Check", width: 30 },
    { label: "Items Taken", width: 50 },
];

/**
 * Usage report - every time someone logged items taken from a box via its QR code: when, who, which
 * box, the tag they removed and the new one they fitted, and what they took. Defaults to the last 6 months.
 */
function UsageReport() {
    const [tableState, setTableState] = useState({ page: 1, perPage: 10, sortKey: "completed_at", sortOrder: "desc" });
    const [search, setSearch] = useState("");
    const [campusFilter, setCampusFilter] = useState(EMPTY_FILTER);
    const [boxFilter, setBoxFilter] = useState(EMPTY_SEARCH_FILTER);
    const [dateRange, setDateRange] = useState({ startDate: moment().subtract(6, "months").toDate(), endDate: new Date() });
    const { data: reportFilters } = useFirstAidReportFilters();

    const reportParams = useMemo(() => ({
        page: tableState.page,
        limit: tableState.perPage,
        key: tableState.sortKey,
        sort: tableState.sortOrder,
        search,
        viaCampus: campusFilter.arr,
        viaBox: boxFilter.arr,
        startDate: dateRange?.startDate ? moment(dateRange.startDate).format("YYYY-MM-DD") : undefined,
        endDate: dateRange?.endDate ? moment(dateRange.endDate).format("YYYY-MM-DD") : undefined,
    }), [tableState, search, campusFilter, boxFilter, dateRange]);
    const { data: usagePage, isFetching } = useFirstAidUsageReport(reportParams);

    // A new filter or search starts from page 1, or the table can land on a page that no longer exists.
    useEffect(() => {
        setTableState((currentState) => ({ ...currentState, page: 1 }));
    }, [search, campusFilter, boxFilter, dateRange]);

    const columns = useMemo(
        () => [
            {
                name: "Date",
                selector: "completed_at",
                sortField: "completed_at",
                sortable: true,
                minWidth: "150px",
                cell: (row) => moment(row.completed_at).format(REPORT_DATE_TIME_FORMAT),
            },
            {
                name: "Used By",
                selector: "opened_by_name",
                sortField: "opened_by_name",
                sortable: true,
                minWidth: "190px",
                cell: (row) => <OhsUserCell user={getPrefixedUser(row, "opened_by", row.opened_by_id)} />,
            },
            {
                name: "Box",
                selector: "box_code",
                sortField: "box_code",
                sortable: true,
                minWidth: "190px",
                cell: (row) => (
                    <div>
                        <Link to={`${OHS_BASE_PATH}/boxes/open/${row.box_id}`}>{row.box_code}</Link>
                        <div><MutedText>{row.campus_name} · {row.building_name} · {row.area_location}</MutedText></div>
                    </div>
                ),
            },
            {
                name: "Tag Removed",
                minWidth: "130px",
                cell: (row) => row.tag_serial_found || <ConditionBadge $condition="damaged">No Tag</ConditionBadge>,
            },
            {
                name: "New Tag",
                minWidth: "120px",
                cell: (row) => row.new_tag_serial || "-",
            },
            {
                name: "Tag Check",
                minWidth: "170px",
                cell: (row) => {
                    const tagCheck = describeTagCheck(row);
                    return <ConditionBadge $condition={tagCheck.isFlagged ? "damaged" : "ok"} title={tagCheck.label}>{tagCheck.isFlagged ? "Flagged" : "Matched"}</ConditionBadge>;
                },
            },
            {
                name: "Items Taken",
                grow: 2,
                minWidth: "240px",
                cell: (row) => (
                    <div>
                        {row.items.map((usedItem) => (
                            <div key={usedItem.box_item_id}>
                                {usedItem.quantity_used} × {usedItem.item_name} <MutedText>({usedItem.quantity} left)</MutedText>
                            </div>
                        ))}
                    </div>
                ),
            },
        ],
        []
    );

    /**
     * Exports every usage matching the current filters (not just this page).
     * @param {string} fileType xlsx | csv | pdf
     * @param {string} fileName Download name.
     */
    const handleExport = async (fileType, fileName) => {
        try {
            const { data } = await GetFirstAidUsageReport({ ...reportParams, exportStatus: "true" });
            const usageRows = data?.data ?? [];

            await exportOhsReport({
                fileType,
                fileName,
                columns: USAGE_EXPORT_COLUMNS,
                rows: usageRows.map((usage) => [
                    moment(usage.completed_at).format("YYYY-MM-DD"),
                    moment(usage.completed_at).format("HH:mm"),
                    usage.opened_by_name,
                    usage.campus_name,
                    usage.building_name,
                    usage.area_location,
                    usage.box_code,
                    usage.tag_serial_found || "No tag",
                    usage.new_tag_serial,
                    { value: describeTagCheck(usage).label, tone: describeTagCheck(usage).isFlagged ? "flagged" : "ok" },
                    usage.items.map((usedItem) => `${usedItem.quantity_used} x ${usedItem.item_name} (${usedItem.quantity} left)`).join("; "),
                ]),
                pdfSections: usageRows.map((usage) => ({
                    title: `${moment(usage.completed_at).format(REPORT_DATE_TIME_FORMAT)} - ${usage.box_code} (${usage.campus_name}, ${usage.building_name}, ${usage.area_location})`,
                    summary: `Used by ${usage.opened_by_name} · Tag removed: ${usage.tag_serial_found || "no tag"} · New tag: ${usage.new_tag_serial} · ${describeTagCheck(usage).label}`,
                    header: ["Item Taken", "Quantity Taken", "Left In Box"],
                    rows: usage.items.map((usedItem) => [usedItem.item_name, usedItem.quantity_used, usedItem.quantity]),
                })),
            });
        } catch (error) {
            console.log("UsageReport.js->handleExport->Error", error);
            Swal.fire("Error", "The usage report could not be exported.", "error");
        }
    };

    return (
        <DataTableComponent
            data={usagePage?.data ?? []}
            loading={isFetching}
            state={tableState}
            setState={setTableState}
            setSearch={setSearch}
            totalRows={usagePage?.total ?? 0}
            columns={columns}
            exportFunction={handleExport}
            exportFileName="First_Aid_Usage_Report"
            filters={[
                {
                    filterName: "Date",
                    state: dateRange,
                    setState: setDateRange,
                    dateRangeFilter: true,
                },
                {
                    filterName: "Campus",
                    optionArr: reportFilters.CampusFilter || [],
                    state: campusFilter,
                    setState: setCampusFilter,
                    uniqueId: "ohsUsageCampus",
                },
                {
                    filterName: "Box",
                    optionArr: reportFilters.BoxFilter || [],
                    state: boxFilter,
                    setState: setBoxFilter,
                    uniqueId: "ohsUsageBox",
                    isSearchFilter: true,
                },
            ]}
        />
    );
}

export default UsageReport;
